import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import ChartTemplate, { ChartTimeSplit } from "@/components/chart-template";
import { LoadingSpinner } from "@/components/loading-spinner";
import { CalendarDates } from "@/custom-types";
import { useAnalysis } from "@/http/api-hooks";
import { AnalysisRow } from "@/http/api-types";
import { formatDateToYYYYMMDD } from "@/lib/utils";
import { useState } from "react";
import { AnalysisChartGranularity, SplitDimension } from ".";
import Chart from "./chart";

type APConversionsToggleType = "none" | "ap" | "secondary";

type AnalysisChartsProps = {
  date?: CalendarDates;
  audience: string;
  campaign: string;
  campaignType: string;
  dimension: SplitDimension;
  granularity: AnalysisChartGranularity;
  shouldFetch: boolean;
};

export type DimensionValues = {
  [key: string]: number;
};

export type AnalysisChartData = {
  [K in ChartTimeSplit]: Record<string, Record<string, Array<{ dateTime: string } & DimensionValues>>>;
};

const metrics = [
  "bids",
  "impressions",
  "sum_buying_price_eur",
  "clicks",
  "wins",
  "ap_conversions",
  "secondary_ap_conversions",
  "conversions",
  "secondary_conversions",
  "cpi",
  "secondary_cpa",
  "buying_ecpm",
  "buying_ecpc",
  "avg_spend_rate",
  "sum_bids_estimated_install_rate",
  "sum_imps_estimated_install_rate",
  "ap_conv_pm",
  "ctr",
  "win_rate",
  "impression_over_bids",
] as const;

type MetricType = (typeof metrics)[number];

const overlayModeLabels: Array<{ key: APConversionsToggleType; label: string }> = [
  { key: "none", label: "None" },
  { key: "ap", label: "AP" },
  { key: "secondary", label: "2nd AP" },
];

const metricLabels: Record<MetricType, string> = {
  bids: "Bids",
  impressions: "Impressions",
  sum_buying_price_eur: "Revenue (EUR)",
  clicks: "Clicks",
  wins: "Wins",
  ap_conversions: "AP Conversions",
  secondary_ap_conversions: "2nd AP Conversions",
  conversions: "Conversions",
  secondary_conversions: "2nd Conversions",
  cpi: "CPI",
  secondary_cpa: "Secondary CPA",
  buying_ecpm: "Buying eCPM",
  buying_ecpc: "Buying eCPC",
  avg_spend_rate: "Avg Spend Rate",
  sum_bids_estimated_install_rate: "Estimated Install Rate on Bids",
  sum_imps_estimated_install_rate: "Estimated Install Rate on Impressions",
  ap_conv_pm: "AP Conv PM",
  ctr: "CTR",
  win_rate: "Win Rate",
  impression_over_bids: "Impression Over Bids",
};

const metricCategories: Record<MetricType, string> = {
  bids: "Volume",
  impressions: "Volume",
  wins: "Volume",
  clicks: "Volume",
  ap_conversions: "Conversions",
  secondary_ap_conversions: "Conversions",
  conversions: "Conversions",
  secondary_conversions: "Conversions",
  sum_buying_price_eur: "Revenue",
  cpi: "Cost",
  secondary_cpa: "Cost",
  buying_ecpm: "Cost",
  buying_ecpc: "Cost",
  avg_spend_rate: "Performance",
  sum_bids_estimated_install_rate: "Performance",
  sum_imps_estimated_install_rate: "Performance",
  ap_conv_pm: "Performance",
  ctr: "Performance",
  win_rate: "Performance",
  impression_over_bids: "Performance",
};

export { metricCategories, metricLabels, metrics };

function formatTimestamp(row: AnalysisRow, timeSplit: ChartTimeSplit): string {
  if (timeSplit === "hourly") {
    return row.bid_day.slice(0, 10) + "T" + row.bid_hour.padStart(2, "0") + (row.bid_hour !== "23" ? ":00" : ":59");
  }
  return row.bid_day.slice(0, 10);
}

function buildMergedData(
  base: Array<Record<string, any>>,
  overlay?: Array<Record<string, any>>,
): Array<Record<string, any>> {
  if (!overlay || overlay.length === 0) return base;
  const map: Record<string, Record<string, any>> = {};
  for (const row of base) {
    const { dateTime, ...rest } = row;
    if (!map[dateTime]) map[dateTime] = { dateTime };
    Object.entries(rest).forEach(([k, v]) => {
      map[dateTime][k] = v;
    });
  }
  for (const row of overlay) {
    const { dateTime, ...rest } = row;
    if (!map[dateTime]) map[dateTime] = { dateTime };
    Object.entries(rest).forEach(([k, v]) => {
      map[dateTime][`${k}__ov`] = v;
    });
  }
  return Object.values(map).sort((a, b) => (a.dateTime as string).localeCompare(b.dateTime as string));
}

function AnalysisCharts({
  date,
  audience,
  campaign,
  campaignType,
  dimension,
  granularity,
  shouldFetch,
}: AnalysisChartsProps) {
  const [selectedDimensions, setSelectedDimensions] = useState<Record<string, Set<string>>>({});
  const [overlayModes, setOverlayModes] = useState<Record<string, APConversionsToggleType>>({});

  const setOverlayMode = (metric: string, mode: APConversionsToggleType) => {
    setOverlayModes(prev => ({ ...prev, [metric]: mode }));
  };

  const {
    data: analysisData,
    isLoading: analysisLoading,
    error: analysisError,
  } = useAnalysis(
    {
      audience_id: audience,
      start_date: date?.from && formatDateToYYYYMMDD(date.from),
      end_date: date?.to && formatDateToYYYYMMDD(date.to),
      campaign_id: campaign,
      campaign_type: campaignType,
      dimension: dimension,
      granularity: granularity,
    },
    shouldFetch,
  );

  if (analysisError) {
    return <div className="text-md text-destructive">Error loading analysis data</div>;
  }

  if (analysisLoading) {
    return (
      <div className="w-full flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (!analysisData) {
    return null;
  }

  if (!analysisData.results || analysisData.results.length === 0) {
    return <div className="text-md text-muted-foreground">No data available for the selected filters</div>;
  }

  const data = analysisData.results;

  const hourlyData = transformForRecharts(data, "hourly");
  const dailyData = transformForRecharts(data, "daily");

  const chartData: AnalysisChartData = {
    hourly: hourlyData,
    daily: dailyData,
  };

  // Precompute AP and 2nd AP conversion totals (grand totals & per dimension)
  const apTotals = calculateTotalMetrics(data, "ap_conversions", dimension);
  const secondaryApTotals = calculateTotalMetrics(data, "secondary_ap_conversions", dimension);
  const apGrandTotal = Object.values(apTotals).reduce((a, b) => a + b, 0);
  const secondaryApGrandTotal = Object.values(secondaryApTotals).reduce((a, b) => a + b, 0);

  const formatter = new Intl.NumberFormat("en", { notation: "compact" });
  const overlayFormatter = new Intl.NumberFormat("en", { notation: "standard" });
  const pctFormatter = new Intl.NumberFormat("en", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const toggleDimensionSelection = (metric: string, dimensionValue: string) => {
    setSelectedDimensions(prev => {
      const newSelected = { ...prev };

      if (!newSelected[metric]) {
        newSelected[metric] = new Set([dimensionValue]);
      } else if (newSelected[metric].has(dimensionValue)) {
        newSelected[metric].delete(dimensionValue);
        if (newSelected[metric].size === 0) {
          delete newSelected[metric];
        }
      } else {
        newSelected[metric].add(dimensionValue);
      }

      return newSelected;
    });
  };

  const clearSelections = (metric: string) => {
    setSelectedDimensions(prev => {
      const newSelected = { ...prev };
      delete newSelected[metric];
      return newSelected;
    });
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      {metrics.map(metric => {
        const totalMetrics = calculateTotalMetrics(data, metric, dimension);
        const grandTotal = Object.values(totalMetrics).reduce((a, b) => a + b, 0);

        // Sort dimension values by metric value for consistent color assignment
        const sortedDimensionValues = Object.entries(totalMetrics)
          .sort((a, b) => b[1] - a[1]) // Sort by value in descending order
          .map(([dimensionValue]) => dimensionValue);

        const hasSelectedDimensions = selectedDimensions[metric]?.size > 0;

        const overlayModeCurrent = overlayModes[metric] || "none";
        const baseData = chartData[granularity][dimension][metric] || [];
        const overlayMetricKey =
          overlayModeCurrent === "ap"
            ? "ap_conversions"
            : overlayModeCurrent === "secondary"
              ? "secondary_ap_conversions"
              : null;
        const overlaySeries = overlayMetricKey ? chartData[granularity][dimension][overlayMetricKey] : undefined;
        const mergedData = buildMergedData(baseData, overlaySeries);

        const baseDimensionValues: string[] = [];
        baseData.forEach(row => {
          Object.keys(row)
            .filter(k => k !== "dateTime")
            .forEach(k => {
              if (!baseDimensionValues.includes(k)) baseDimensionValues.push(k);
            });
        });

        const showOverlay = !!overlaySeries;

        return (
          <div key={metric} id={`metric-${metric}`} className="flex flex-row gap-3 w-full h-full">
            <div className="w-3/4 min-w-0 h-full flex flex-col">
              <ChartTemplate
                timeSplittable={false}
                table={null}
                title={
                  <div className="flex justify-between items-center w-full">
                    <span>{metricLabels[metric]}</span>
                    <div className="min-w-[160px] flex items-start justify-end gap-2">
                      <div className="flex flex-col items-end gap-1">
                        <div className="text-[14px] font-medium text-muted-foreground leading-none pb-1">
                          AP Conversions
                        </div>
                        <div className="flex rounded border border-border overflow-hidden">
                          {overlayModeLabels.map(opt => {
                            const active = (overlayModes[metric] || "none") === opt.key;
                            return (
                              <button
                                key={opt.key}
                                onClick={() => setOverlayMode(metric, opt.key as APConversionsToggleType)}
                                className={`px-1.5 py-0.5 text-[12px] font-medium transition-colors ${
                                  active
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-background hover:bg-secondary text-muted-foreground"
                                }`}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                }
                chart={
                  <Chart
                    data={mergedData}
                    yAxisKey={metric}
                    granularity={granularity}
                    baseDimensionValues={baseDimensionValues}
                    sortedDimensionValues={sortedDimensionValues}
                    selectedDimensions={selectedDimensions[metric]}
                    overlayMode={overlayModeCurrent}
                    showOverlay={showOverlay}
                  />
                }
              />
            </div>

            <div className="w-1/4 flex-shrink-0 h-full flex flex-col">
              <Card className="shadow-md h-full flex flex-col">
                <CardHeader className="pb-1">
                  <CardTitle className="flex justify-between items-center text-sm">
                    <span className="text-sm">{metricLabels[metric]}</span>
                    <div className="h-6 min-w-[50px] flex items-center justify-end">
                      {hasSelectedDimensions && (
                        <button
                          onClick={() => clearSelections(metric)}
                          className="text-xs text-muted-foreground hover:text-primary transition-colors px-1.5 py-0.5 rounded-md hover:bg-secondary"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 p-3 pt-0 flex flex-col overflow-hidden">
                  <div className="overflow-y-auto h-full">
                    <div className="space-y-1 pr-1">
                      {Object.keys(totalMetrics).length > 0 ? (
                        Object.entries(totalMetrics)
                          .sort((a, b) => b[1] - a[1]) // Sort by value in descending order
                          .map(([dimensionValue, value], index) => {
                            const isSelected = selectedDimensions[metric]?.has(dimensionValue);
                            const isFiltered = hasSelectedDimensions && !isSelected;
                            const pct = grandTotal > 0 ? (value / grandTotal) * 100 : 0;
                            const apValue = apTotals[dimensionValue] || 0;
                            const secondaryApValue = secondaryApTotals[dimensionValue] || 0;
                            const apPct = apGrandTotal > 0 ? (apValue / apGrandTotal) * 100 : 0;
                            const secondaryApPct =
                              secondaryApGrandTotal > 0 ? (secondaryApValue / secondaryApGrandTotal) * 100 : 0;

                            return (
                              <div
                                key={dimensionValue}
                                className={`flex justify-between items-center py-1 ${
                                  index !== Object.keys(totalMetrics).length - 1 ? "border-b border-border/50" : ""
                                } ${isFiltered ? "opacity-40" : ""} cursor-pointer hover:bg-secondary/20 rounded px-1 transition-colors`}
                                onClick={() => toggleDimensionSelection(metric, dimensionValue)}
                              >
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <div
                                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isSelected ? "ring-1 ring-primary ring-offset-1" : ""}`}
                                    style={{ backgroundColor: `hsl(var(--chart-${(index % 20) + 1}))` }}
                                  ></div>
                                  <span className="text-xs font-medium truncate">{dimensionValue}</span>
                                </div>
                                <div className="flex flex-col items-end flex-shrink-0 ml-1 tabular-nums">
                                  <span
                                    className="text-xs font-semibold"
                                    title={`${value} (${pctFormatter.format(pct)}%)`}
                                  >
                                    {formatter.format(value)} ({pctFormatter.format(pct)}%)
                                  </span>
                                  <div className="hidden md:flex gap-2">
                                    <span
                                      className="text-[10px] font-medium text-muted-foreground"
                                      title={`AP Conversions: ${apValue} (${pctFormatter.format(apPct)}%)`}
                                    >
                                      AP {overlayFormatter.format(apValue)} ({pctFormatter.format(apPct)}%)
                                    </span>
                                    <span
                                      className="text-[10px] font-medium text-muted-foreground"
                                      title={`2nd AP Conversions: ${secondaryApValue} (${pctFormatter.format(secondaryApPct)}%)`}
                                    >
                                      2nd {overlayFormatter.format(secondaryApValue)} (
                                      {pctFormatter.format(secondaryApPct)}%)
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                      ) : (
                        <div className="text-xs text-muted-foreground text-center py-3">No total data available</div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default AnalysisCharts;

function transformForRecharts(
  data: AnalysisRow[],
  timeSplit: ChartTimeSplit,
): Record<string, Record<string, Array<{ dateTime: string } & DimensionValues>>> {
  const result: Record<string, Record<string, Array<{ dateTime: string } & DimensionValues>>> = {};

  data
    .filter(row => row.aggregation_level === timeSplit)
    .forEach(row => {
      const dateTime = formatTimestamp(row, timeSplit);
      const metric_value = Number(row.metric_value);

      if (!result[row.dimension_type]) result[row.dimension_type] = {};
      if (!result[row.dimension_type][row.metric_type]) result[row.dimension_type][row.metric_type] = [];

      const hourEntry = result[row.dimension_type][row.metric_type].find(entry => entry.dateTime === dateTime);
      if (hourEntry) {
        hourEntry[row.dimension_value] = (hourEntry[row.dimension_value] || 0) + metric_value;
      } else {
        result[row.dimension_type][row.metric_type].push(
          Object.assign({ dateTime }, { [row.dimension_value]: metric_value }),
        );
      }
    });

  // Sort hours numerically for proper chart x-axis order
  for (const dimension in result) {
    for (const metric in result[dimension]) {
      result[dimension][metric].sort((a, b) => a.dateTime.localeCompare(b.dateTime));
    }
  }

  return result;
}

function calculateTotalMetrics(
  data: AnalysisRow[],
  metric: string,
  dimension: SplitDimension,
): { [key: string]: number } {
  const totals: { [key: string]: number } = {};

  data
    .filter(row => row.aggregation_level === "total" && row.metric_type === metric && row.dimension_type === dimension)
    .forEach(row => {
      const dimensionValue = row.dimension_value;
      const metricValue = Number(row.metric_value);

      // Sum the metric values for each dimension value
      if (!totals[dimensionValue]) {
        totals[dimensionValue] = 0;
      }
      totals[dimensionValue] += metricValue;
    });

  return totals;
}
